import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Clock, Briefcase, Users, DollarSign, Send, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const JobDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    portfolio: '',
    coverLetter: '',
    cv: null
  });

  // Mock job data - gerçek uygulamada API'den gelecek
  const jobs = {
    'senior-frontend-developer': {
      title: 'Senior Frontend Developer',
      department: 'Yazılım Geliştirme',
      location: 'İstanbul / Hybrid',
      type: 'Tam Zamanlı',
      experience: '5+ yıl',
      salary: '₺40.000 - ₺60.000',
      description: 'React ve modern frontend teknolojileri konusunda deneyimli, kullanıcı deneyimine önem veren bir Senior Frontend Developer arıyoruz.',
      responsibilities: [
        'React, Next.js ile modern web uygulamaları geliştirmek',
        'UI/UX tasarımlarını pixel-perfect şekilde kodlamak',
        'Performance optimizasyonu ve best practices uygulamak',
        'Junior geliştiricilere mentorluk yapmak',
        'Code review süreçlerine aktif katılım',
        'Teknik dokümantasyon hazırlamak'
      ],
      requirements: [
        '5+ yıl frontend geliştirme deneyimi',
        'React, TypeScript, Next.js konusunda ileri seviye bilgi',
        'Tailwind CSS, styled-components gibi CSS framework deneyimi',
        'Git, CI/CD süreçleri hakkında bilgi',
        'RESTful API ve GraphQL deneyimi',
        'Responsive ve mobile-first tasarım prensipleri',
        'İngilizce okuma ve yazma yeteneği'
      ],
      niceToHave: [
        'Node.js backend deneyimi',
        'AWS, Docker, Kubernetes bilgisi',
        'Open source projelere katkı',
        'Teknik blog yazarlığı',
        'Konferans konuşmaları'
      ],
      benefits: [
        'Rekabetçi maaş paketi',
        'Esnek çalışma saatleri',
        'Hybrid çalışma modeli',
        'Sağlık sigortası',
        'Yemek kartı',
        'Eğitim ve konferans desteği',
        'Yıllık 20 gün izin',
        'Doğum günü izni',
        'Teknoloji ekipmanı desteği'
      ]
    },
    'ui-ux-designer': {
      title: 'UI/UX Designer',
      department: 'Tasarım',
      location: 'İstanbul / Remote',
      type: 'Tam Zamanlı',
      experience: '3+ yıl',
      salary: '₺30.000 - ₺45.000',
      description: 'Kullanıcı odaklı, yaratıcı ve detaycı bir UI/UX Designer arıyoruz.',
      responsibilities: [
        'Kullanıcı araştırması ve persona oluşturma',
        'Wireframe ve prototip tasarımı',
        'High-fidelity UI tasarımları',
        'Design system oluşturma ve yönetimi',
        'Usability testing',
        'Geliştiricilerle yakın işbirliği'
      ],
      requirements: [
        '3+ yıl UI/UX tasarım deneyimi',
        'Figma, Adobe XD konusunda ileri seviye',
        'User research metodolojileri bilgisi',
        'Responsive ve mobile tasarım deneyimi',
        'Portfolio zorunlu',
        'İngilizce okuma ve yazma'
      ],
      niceToHave: [
        'HTML/CSS bilgisi',
        'Motion design deneyimi',
        'Illustration yetenekleri',
        'Branding deneyimi'
      ],
      benefits: [
        'Rekabetçi maaş',
        'Tam remote çalışma imkanı',
        'Sağlık sigortası',
        'Eğitim desteği',
        'Adobe Creative Cloud lisansı',
        'Esnek çalışma saatleri'
      ]
    }
  };

  const job = jobs[slug];

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">İlan Bulunamadı</h1>
          <Link to="/kariyer">
            <Button>Kariyer Sayfasına Dön</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Job application submitted:', formData);
    
    toast({
      title: "Başvurunuz Alındı! 🎉",
      description: "Ekibimiz başvurunuzu değerlendirip en kısa sürede dönüş yapacak.",
    });

    setTimeout(() => {
      navigate('/tesekkurler?type=contact');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back Button */}
          <Link to="/kariyer" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tüm İlanlar
          </Link>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                <h1 className="text-4xl font-bold text-white mb-4">{job.title}</h1>
                <div className="flex flex-wrap gap-4 text-gray-400">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    <span>{job.department}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{job.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{job.type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{job.experience}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    <span>{job.salary}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Pozisyon Hakkında</h2>
                <p className="text-gray-400 leading-relaxed">{job.description}</p>
              </div>

              {/* Responsibilities */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Sorumluluklar</h2>
                <ul className="space-y-3">
                  {job.responsibilities.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-400">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Aranan Nitelikler</h2>
                <ul className="space-y-3">
                  {job.requirements.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-400">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Nice to Have */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Artı Değerler</h2>
                <ul className="space-y-3">
                  {job.niceToHave.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-400">
                      <span className="w-2 h-2 bg-teal-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Benefits */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Yan Haklar</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {job.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-400">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar - Application Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-slate-800/40 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-6">
                <h3 className="text-2xl font-bold text-white mb-6">Başvuru Yap</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Ad Soyad *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">E-posta *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-white mb-2 block">Telefon</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="linkedin" className="text-white mb-2 block">LinkedIn</Label>
                    <Input
                      id="linkedin"
                      value={formData.linkedin}
                      onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                      className="bg-slate-900/50 border-slate-700 text-white"
                      placeholder="linkedin.com/in/..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="portfolio" className="text-white mb-2 block">Portfolio/GitHub</Label>
                    <Input
                      id="portfolio"
                      value={formData.portfolio}
                      onChange={(e) => setFormData({ ...formData, portfolio: e.target.value })}
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="coverLetter" className="text-white mb-2 block">Ön Yazı</Label>
                    <Textarea
                      id="coverLetter"
                      rows={4}
                      value={formData.coverLetter}
                      onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                      className="bg-slate-900/50 border-slate-700 text-white resize-none"
                      placeholder="Neden bu pozisyon için uygun olduğunuzu anlatın..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="cv" className="text-white mb-2 block">CV Yükle *</Label>
                    <Input
                      id="cv"
                      type="file"
                      required
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setFormData({ ...formData, cv: e.target.files[0] })}
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                    <p className="text-gray-500 text-xs mt-1">PDF, DOC veya DOCX (Max 5MB)</p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                  >
                    Başvuru Yap
                    <Send className="w-4 h-4 ml-2" />
                  </Button>

                  <p className="text-gray-500 text-xs text-center">
                    Başvurunuz gizlilik politikamız kapsamında değerlendirilecektir
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JobDetail;
